import React from "react";
import { useSelector } from "react-redux";

export default function VoidReceiptPrintPage() {
  const billData = useSelector((state) => state.bill.data);
  const { branch } = useSelector((state) => state.pos);

  const formatNumber = (value) => {
    if (value === undefined || value === null || value === "-") return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getVatDisplayText = () => {
    if (branch?.branch_info?.business?.settingVatCalculation?.code) {
      const vatCalculator =
        branch.branch_info.business.settingVatCalculation.code;
      if (vatCalculator === "INCLUDED") return "(VAT Included)";
      if (vatCalculator === "SEPARATE") return "(VAT Excluded)";
    }
    return "";
  };

  const getBusinessInfo = () => {
    if (!billData?.order) {
      return {
        name: "-",
        company: "-",
        taxId: "-",
        branchName: "-",
        branchCode: "",
        logoUrl: "",
      };
    }

    const order = billData.order;
    const branchPos = order.branchPosSelling?.branchPos || {};
    const branchData = branchPos.branch || {};
    const business = branchData.business || {};
    const logoUrl = business.image_path || "";

    return {
      name: business.company_name || "-",
      company: business.company_name || "-",
      taxId: business.tax_number || "-",
      branchName: branchData.branch_name || "-",
      branchCode: branchData.code ? `(${branchData.code})` : "",
      logoUrl: logoUrl,
    };
  };

  const getVoidDetails = () => {
    if (!billData) {
      return {
        date: "-",
        posId: "-",
        voidNo: "-",
        staffName: "-",
        reason: "-",
      };
    }

    const user = billData.userBusinessGroup || {};
    const voidRemark = billData.billVoidRemark || {};

    const dateObj = new Date(billData.voided_at || new Date());
    const formattedDate = `${dateObj.getDate().toString().padStart(2, "0")}/${(
      dateObj.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${dateObj.getFullYear()} ${dateObj
      .getHours()
      .toString()
      .padStart(2, "0")}:${dateObj.getMinutes().toString().padStart(2, "0")}`;

    const staffName =
      user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.first_name || "ไต";

    return {
      date: formattedDate,
      posId: billData.order?.branchPosSelling?.branchPos?.pos_id || "PO145740",
      voidNo: billData.void_no || "VO20250615P1",
      receiptNo: billData.receipt_no || billData.order?.code || "R2309000002P1",
      staffName: staffName,
      reason: voidRemark.name || "ลูกค้าขอยกเลิก",
    };
  };

  const getBillItems = () => {
    if (!billData || !billData.billItems) {
      return [];
    }

    return billData.billItems.map((item) => {
      const orderItem = item.orderItem || {};
      const product = orderItem.product || {};
      const options = orderItem.orderProductOption || [];
      const specificOptions = orderItem.orderProductSpecificOption || [];
      const annotations = orderItem.orderProductAnnotation || [];

      return {
        id: item.id,
        quantity: item.quantity,
        name: product.name || "-",
        price: formatNumber(item.price),
        pricePerPiece: formatNumber(
          billData.order.order_type === "DELIVERY"
            ? (item.orderItem.product?.branchProduct &&
              item.orderItem.product?.branchProduct.length > 0 &&
              item.orderItem.product?.branchProduct[0].data?.price_delivery
                ? item.orderItem.product?.branchProduct[0].data?.price_delivery
                : item.orderItem.product?.branchProduct[0].data?.price ||
                  item.orderItem.product?.price_delivery) || 0
            : (item.orderItem.product?.branchProduct &&
              item.orderItem.product?.branchProduct.length > 0 &&
              item.orderItem.product?.branchProduct[0].data?.price
                ? item.orderItem.product?.branchProduct[0].data?.price
                : item.orderItem.product?.branchProduct[0].data?.price ||
                  item.orderItem.product?.price) || 0
        ),
        total: item.price,
        options: [
          ...options.map((opt) => ({
            name: opt.productOptionItem?.name || "",
            price:
              parseFloat(opt.qty || 0) *
              parseFloat(opt.productOptionItem?.price || 0),
          })),
          ...specificOptions.map((opt) => ({
            name: opt.productSpecificOptionItem?.name || "",
            price:
              parseFloat(opt.qty || 0) *
              parseFloat(opt.productSpecificOptionItem?.price || 0),
          })),
        ],
        annotations: annotations.map(
          (ann) => ann.productAnnotationItem?.name || ""
        ),
      };
    });
  };

  const getMemberInfo = () => {
    const memberData = billData?.member;

    if (!memberData) {
      return {
        id: "-",
        name: "-",
        phone: "-",
      };
    }

    return {
      id: memberData.code || "-",
      name:
        `${memberData.first_name_th || ""} ${
          memberData.last_name_th || ""
        }`.trim() || "-",
      phone: memberData.phone_number || "-",
    };
  };

  const getTotals = () => {
    if (!billData) {
      return {
        subtotal: "0.00",
      };
    }

    return {
      subtotal: formatNumber(billData.total_before_discount || 0),
    };
  };

  const renderProductOptions = (options, quantity) => {
    if (!options || options.length === 0) return null;

    return options.map((option, index) => (
      <div key={index} className="flex w-full mt-1">
        <div className="w-1/4"></div>
        <div className="w-1/2 text-small text-[0.78vw]">
          -&nbsp;{option.name}
        </div>
        <div className="w-1/4 text-small text-[0.78vw] text-right">
          {Number(option.price || "0") > 0 && "+"}
          {Number(option.price || "0") > 0 &&
            formatNumber((option.price || 0) * quantity || 0)}
        </div>
        <div className="w-1/4 text-small text-[0.78vw] text-right"></div>
      </div>
    ));
  };

  const renderProductAnnotations = (annotations) => {
    if (!annotations || annotations.length === 0) return null;

    return annotations.map((annotation, index) => (
      <div key={index} className="flex w-full mt-1">
        <div className="w-1/4"></div>
        <div className="w-1/2 text-small text-[0.78vw]">
          -&nbsp;{annotation}
        </div>
        <div className="w-1/4"></div>
        <div className="w-1/4"></div>
      </div>
    ));
  };

  const business = getBusinessInfo();
  const voidDetails = getVoidDetails();
  const products = getBillItems();
  const member = getMemberInfo();
  const totals = getTotals();

  return (
    <div className="flex justify-center text-[0.78vw] w-full leading-[1.8]">
      <div
        id="section-to-print-receipt-page"
        className="w-[29.49vw] bg-[#FFF] px-[1.70vw] py-[1.31vw] flex flex-col gap-[0.59vw] text-[0.98vw] font-[300]"
      >
        <div>
          <div className="text-center w-full flex flex-col items-center font-[300]">
            {business.logoUrl && (
              <img
                className="object-cover aspect-square w-[7.71vw] h-fit logo-receipt"
                src={business.logoUrl}
                alt=""
              />
            )}

            <div>
              {business.name} - {business.branchName} {business.branchCode}
            </div>

            {business.taxId && (
              <div className="font-large">
                TAX ID : {business.taxId} {getVatDisplayText()}
              </div>
            )}

            <div className="font-[500] mt-[0.51vw]">
              ใบยกเลิกรายการ (ไม่ใช่ใบเสร็จรับเงิน)
            </div>
          </div>

          <div className="flex flex-col gap-[0.39vw]">
            <div className="flex justify-between gap-[0.98vw]">
              <div className="whitespace-nowrap">
                วันที่ : {voidDetails.date}
              </div>
              <div className="text-right">POS# : {voidDetails.posId}</div>
            </div>
            <div className="flex justify-between gap-[0.98vw]">
              <div className="text-left">
                พนักงานขาย : {voidDetails.staffName}
              </div>
            </div>
            <div className="flex justify-between gap-[0.98vw]">
              <div className="whitespace-nowrap">
                REF: {voidDetails.receiptNo}
              </div>
              <div className="text-right">เลขที่ : {voidDetails.voidNo}</div>
            </div>
          </div>

          <div className="flex flex-col gap-[0.39vw]">
            <div className="mt-[0.78vw]">
              <div className="border-dashed border-t-[0.04vw] border-b-[0.04vw] border-black py-[0.39vw] flex">
                <div className="w-1/4 text-center font-[400]">จำนวน</div>
                <div className="w-1/2 text-center font-[400]">รายการ</div>
                <div className="w-1/4 text-right font-[400]">หน่วยละ</div>
                <div className="w-1/4 text-right font-[400]">รวมเงิน</div>
              </div>

              <div className="pt-[0.78vw] pb-[0.29vw] font-[500]">
                จำนวนสินค้า :{" "}
                {products.reduce(
                  (sum, product) => sum + (parseInt(product.quantity) || 0),
                  0
                )}
              </div>

              {products.map((product, index) => (
                <div key={product.id || index} className="mb-3">
                  <div className="flex pt-[0.29vw]">
                    <div className="w-1/4 text-center align-top leading-[1.45]">
                      {product.quantity}
                    </div>
                    <div className="w-1/2 align-top break-words leading-[1.45]">
                      {product.name}
                    </div>
                    <div className="w-1/4 text-right align-top leading-[1.45]">
                      {product.pricePerPiece || 0}
                    </div>
                    <div className="w-1/4 text-right align-top leading-[1.45]">
                      {formatNumber(Number(product.total || 0))}
                    </div>
                  </div>

                  {renderProductOptions(product.options, product.quantity)}
                  {renderProductAnnotations(product.annotations)}
                </div>
              ))}

              <div className="flex text-large font-[700]">
                <div className="w-1/4"></div>
                <div className="w-1/2">ยอดรวม</div>
                <div className="w-1/4"></div>
                <div className="w-1/4 text-right">{totals.subtotal}</div>
              </div>

              {console.log("voidDetails : ", voidDetails)}
              <div className="text-left font-[500] mb-8 mt-[0.78vw]">
                เหตุผลการ VOID : {voidDetails.reason} {"  "}
                {billData?.remark || ""}
              </div>

              {member.id !== "-" ? (
                <div>
                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                  <div className="text-center">Member : {member.id}</div>

                  <div className="flex justify-between gap-[0.98vw] mt-[1.66vw]">
                    <div className="whitespace-nowrap">คุณ : {member.name}</div>
                    <div className="text-right">เบอร์ : {member.phone}</div>
                  </div>

                  <div className="flex mt-[0.78vw]">
                    <div className="w-1/4 font-[400]"></div>
                    <div className="w-1/4 text-right font-[400]">ได้รับ</div>
                    <div className="w-1/4 text-right font-[400]">ใช้ไป</div>
                    <div className="w-1/4 text-right font-[400]">คงเหลือ</div>
                  </div>

                  <div className="flex">
                    <div className="w-1/4">คะแนน</div>
                    <div className="w-1/4 text-right">
                      {billData?.additional_data?.void_point?.earned
                        ? billData.additional_data.void_point.earned
                        : "- 4"}
                    </div>
                    <div className="w-1/4 text-right">
                      {billData?.additional_data?.void_point?.spend || "0"}
                    </div>
                    <div className="w-1/4 text-right">
                      {billData?.additional_data?.void_point?.remain || "3646"}
                    </div>
                  </div>

                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                </div>
              ) : (
                <>
                  <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
                </>
              )}

              <div className="text-center">THANK YOU</div>
              <div className="text-center">ขอบคุณที่ใช้บริการ</div>

              <div className="py-7"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
